/**
 * Partnership page → Placements tab → "Website pages": this campaign's
 * version of the placement map. Move sections up / down (e.g. the brand
 * logos right under the hero for Black Friday), hide them, or show your own
 * mockup instead of the default picture. Frames move along with their
 * section; the default map (Settings) is never changed from here.
 */
import { useState } from 'react';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';
import SectionStack from '../placements/SectionStack';
import { useCropper } from '../placements/useCropper';
import {
  CampaignMapPage,
  pastedPicture,
  readPicture,
  useCampaignMap,
  useCampaignMapMutations,
} from '../../lib/api/placementMap';

const STYLES = `
.cp { display: grid; grid-template-columns: minmax(340px, 440px) minmax(0, 1fr); gap: 16px; align-items: start; }
@media (max-width: 900px) { .cp { grid-template-columns: 1fr; } }
.cp-list { display: grid; gap: 4px; }
.cp-row { display: grid; grid-template-columns: 1fr auto; gap: 8px; align-items: center; padding: 6px 8px; border: 1px solid var(--border-soft); border-radius: 8px; background: var(--bg-elevated); font-size: 12.5px; }
.cp-row.off { opacity: 0.55; background: var(--bg-subtle); }
.cp-row .nm { font-weight: 550; color: var(--ink-2); min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cp-row .sub { font-size: 11px; color: var(--ink-4); font-weight: 400; }
.cp-row .acts { display: flex; gap: 2px; align-items: center; }
.cp-row .acts button, .cp-row .acts label { border: 0; background: transparent; cursor: pointer; padding: 3px 6px; border-radius: 6px; font: inherit; font-size: 12px; color: var(--ink-3); }
.cp-row .acts button:hover, .cp-row .acts label:hover { background: var(--bg-subtle); color: var(--ink); }
.cp-row .acts button:disabled { opacity: 0.3; cursor: default; }
.cp-row .acts label.mock { display: inline-flex; align-items: center; gap: 4px; border: 1px solid var(--border); background: var(--bg-elevated); font-weight: 600; white-space: nowrap; }
.cp-preview { max-height: 720px; overflow: auto; border-radius: 10px; }
`;

export default function CampaignPagesPanel({ campaignId, highlight }: { campaignId: string; highlight?: Set<string> }) {
  const toast = useToast();
  const map = useCampaignMap(campaignId);
  const m = useCampaignMapMutations(campaignId);
  const { crop, element: cropper } = useCropper();
  const [pageKey, setPageKey] = useState('home');
  const page: CampaignMapPage | undefined = map.data?.find((p) => p.key === pageKey) ?? map.data?.[0];

  if (map.isLoading) return <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--ink-4)' }}>Loading the website pages…</div>;
  if (!page) return null;

  const save = async (sections: CampaignMapPage['sections'], extra?: { id: number; image: string; width?: number; height?: number }) => {
    try {
      await m.savePage.mutateAsync({
        page: page.key,
        sections: sections.map((s) => ({
          id: s.id,
          hidden: s.hidden,
          ...(extra && extra.id === s.id ? { image: extra.image, width: extra.width, height: extra.height } : {}),
        })),
      });
    } catch {
      toast.push('Could not save the page layout.');
    }
  };
  const move = (i: number, dir: -1 | 1) => {
    const next = [...page.sections];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    save(next);
  };
  const toggle = (i: number) => save(page.sections.map((s, k) => (k === i ? { ...s, hidden: !s.hidden } : s)));
  const mockup = async (i: number, file: File | undefined) => {
    if (!file) return;
    try {
      const pic = await crop(await readPicture(file));
      if (!pic) return;
      await save(page.sections, { id: page.sections[i].id, ...pic });
      toast.push('Mockup saved for this campaign.');
    } catch (err) {
      toast.push(err instanceof Error ? err.message : 'Could not read the picture.');
    }
  };
  const resetPicture = (i: number) => save(page.sections, { id: page.sections[i].id, image: '' });
  const visible = page.sections.filter((s) => !s.hidden);

  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <style>{STYLES}</style>
      {cropper}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <strong style={{ fontSize: 14 }}>Website pages for this campaign</strong>
        <span style={{ fontSize: 12, color: 'var(--ink-4)', flex: '1 1 300px' }}>
          Reorder, hide or swap in your own mockup — the frames move with their section. The default lives in Settings → Placements &amp; prices → Placement map.
        </span>
        <Button variant="ghost" onClick={() => m.resetPage.mutate(page.key)} disabled={m.resetPage.isPending}>
          Back to the default
        </Button>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {(map.data ?? []).map((p) => (
          <button key={p.key} type="button" className={`filter-pill ${p.key === page.key ? 'active' : ''}`} onClick={() => setPageKey(p.key)}>
            {p.title}
          </button>
        ))}
      </div>
      <div className="cp">
        <div className="cp-list">
          {page.sections.map((s, i) => (
            <div key={s.id} className={`cp-row${s.hidden ? ' off' : ''}`}>
              <div className="nm" title={s.label}>
                {s.label}
                <div className="sub">
                  {[s.hidden ? 'hidden' : '', s.own_image ? 'own mockup' : '', s.frames.length ? `${s.frames.length} frame${s.frames.length === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ') || ' '}
                </div>
              </div>
              <div className="acts">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === page.sections.length - 1} aria-label="Move down">↓</button>
                <button type="button" onClick={() => toggle(i)} title={s.hidden ? 'Show on this campaign' : 'Hide on this campaign'}>
                  <Icon name={s.hidden ? 'x' : 'eye'} size={12} />
                </button>
                <label className="mock" title="Use your own mockup for this campaign">
                  <Icon name="image" size={12} /> {s.own_image ? 'Replace mockup…' : 'Own mockup…'}
                  <input type="file" accept="image/*" hidden onChange={(e) => { const input = e.currentTarget; mockup(i, input.files?.[0]).finally(() => { input.value = ''; }); }} />
                </label>
                <button
                  type="button"
                  title="Paste a screenshot from the clipboard as this campaign's mockup"
                  onClick={async () => {
                    try { await mockup(i, await pastedPicture()); } catch (err) { toast.push(err instanceof Error ? err.message : 'Could not paste.'); }
                  }}
                >
                  Paste
                </button>
                <button
                  type="button"
                  title="Crop this section's picture for this campaign"
                  onClick={async () => {
                    const sec = page.sections[i];
                    const pic = await crop({ image: sec.image, width: sec.width, height: sec.height });
                    if (pic && pic.image !== sec.image) await save(page.sections, { id: sec.id, ...pic });
                  }}
                >
                  Crop
                </button>
                {s.own_image && (
                  <button type="button" onClick={() => resetPicture(i)} title="Back to the default picture">↺ Default</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="cp-preview">
          <SectionStack sections={visible} highlight={highlight} />
        </div>
      </div>
    </div>
  );
}
